import React, { useEffect } from "react";
import usePlacesAutocomplete from "use-places-autocomplete";
import LocationAutocomplete from "../views/LocationAutoComplete.js";

/**
 * Country-aware address autocomplete: THE location field for a vendor's
 * pickup address (brands, brand outlets, shops and wholesalers).
 *
 * Google Places biases suggestions to the market passed in (a registry entry
 * from config/markets.js): no hardcoded "ug"/Kampala. usePlacesAutocomplete
 * captures its requestOptions on first render, so this lives in its own
 * component and the parent remounts it via key={market.country} whenever the
 * market changes. On select it geocodes (inside LocationAutocomplete) and
 * hands back { address, latitude, longitude } so the record carries real GPS
 * coordinates.
 *
 * Mounted by BrandManagement (brand create/edit), BrandOutletFormModal
 * (outlet create/edit) and EditRetailerWholesalerForm (shop edit, from the
 * admin directory and from the shop's own account page). Named for what it
 * does, since it serves more than brands (BrandAddressAutocomplete until
 * 1 Nov 2026).
 */
const VendorAddressAutocomplete = ({ market, mapsApiLoaded, defaultValue, onSelect, onClear }) => {
  const {
    value,
    ready,
    suggestions: { data, loading: placesLoading, status: suggestionStatus } = { data: [] },
    setValue,
    clearSuggestions,
    status,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: market.geocodeCountry },
      location: {
        lat: () => market.mapCenter.lat,
        lng: () => market.mapCenter.lng,
      },
      radius: 200 * 1000,
      types: ["establishment"],
    },
    debounce: 100,
  });

  useEffect(() => {
    if (status === "ERROR") {
      console.error("[VendorAddressAutocomplete] suggestions error:", status);
    }
  }, [status]);

  return (
    <LocationAutocomplete
      value={value}
      setValue={setValue}
      ready={ready}
      data={data}
      clearSuggestions={clearSuggestions}
      mapsApiLoaded={mapsApiLoaded}
      loadingSuggestions={placesLoading} suggestionStatus={suggestionStatus}
      defaultValue={defaultValue}
      onLocationSelect={onSelect}
      onClear={onClear}
    />
  );
};

export default VendorAddressAutocomplete;
